import {
  evaluateCallsRuntimeHandlerCertification,
  type CallsRuntimeHandlerOwner,
} from './CallsRuntimeHandlerCertificationBridge';
import {
  publishGovernorRuntimeEvidence,
  type GovernorRuntimeEvidenceEvent,
} from '../runtime-evidence/GovernorRuntimeEvidenceBus';
import {
  recordRuntimeEvidence,
} from '../runtime-evidence/RuntimeEvidenceEngine';
import {
  emitCallsRuntimeEvent,
} from '../../lib/governorEventBus';

export type CallsRuntimeObservationListener = (
  observations: readonly CallsRuntimeObservation[],
) => void;

export interface CallsRuntimeObservation {
  readonly id: string;
  readonly bridge: 'CallsRuntimeObservationBridge';
  readonly owner: CallsRuntimeHandlerOwner;
  readonly operation: string;
  readonly mode: 'observe';
  readonly certificationDecision: string;
  readonly certificationAllowed: boolean;
  readonly runtimeAllowed: boolean;
  readonly reasonCode: string;
  readonly reason: string;
  readonly observedAt: string;
  readonly sharedEvidenceId: string | null;
  readonly runtimeEvidenceRecorded: boolean;
}

const MAX_CALLS_RUNTIME_OBSERVATIONS = 250;

const observations: CallsRuntimeObservation[] = [];

const observationListeners =
  new Set<CallsRuntimeObservationListener>();

function notifyCallsRuntimeObservationListeners(): void {
  const snapshot = getCallsRuntimeObservations();

  for (const listener of observationListeners) {
    try {
      listener(snapshot);
    } catch {
      // Observation consumers must never interrupt Calls runtime.
    }
  }
}

function createObservationId(): string {
  const randomPart =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : Math.random().toString(36).slice(2);

  return `calls-observation-${Date.now()}-${randomPart}`;
}

function retainBoundedObservationHistory(): void {
  if (observations.length <= MAX_CALLS_RUNTIME_OBSERVATIONS) {
    return;
  }

  observations.splice(
    0,
    observations.length - MAX_CALLS_RUNTIME_OBSERVATIONS,
  );
}

function publishCallsObservationEvidence(input: {
  readonly id: string;
  readonly owner: CallsRuntimeHandlerOwner;
  readonly operation: string;
  readonly certificationDecision: string;
  readonly certificationAllowed: boolean;
  readonly runtimeAllowed: boolean;
  readonly reasonCode: string;
  readonly reason: string;
  readonly observedAt: string;
}): Readonly<{
  sharedEvidence: GovernorRuntimeEvidenceEvent | null;
  runtimeEvidenceRecorded: boolean;
}> {
  let sharedEvidence: GovernorRuntimeEvidenceEvent | null = null;
  let runtimeEvidenceRecorded = false;

  try {
    sharedEvidence = publishGovernorRuntimeEvidence({
      domain: 'calls',
      source: 'CallsRuntimeObservationBridge',
      severity:
        input.certificationDecision === 'block'
          ? 'warn'
          : 'info',
      summary:
        `Calls runtime owner ${input.owner} was observed for ` +
        `${input.operation}.`,
      evidence: [
        `observationId=${input.id}`,
        `owner=${input.owner}`,
        `operation=${input.operation}`,
        'mode=observe',
        `certificationDecision=${input.certificationDecision}`,
        `certificationAllowed=${input.certificationAllowed}`,
        `runtimeAllowed=${input.runtimeAllowed}`,
        `reasonCode=${input.reasonCode}`,
        `observedAt=${input.observedAt}`,
      ],
      owner: input.owner,
      repairReady: false,
    });
  } catch {
    sharedEvidence = null;
  }

  try {
    recordRuntimeEvidence({
      id: input.id,
      governor: 'calls-governor',
      source: 'CallsRuntimeObservationBridge',
      event: 'CALLS_RUNTIME_HANDLER_OBSERVED',
      severity:
        input.certificationDecision === 'block'
          ? 'warning'
          : 'info',
      timestamp: input.observedAt,
      metadata: {
        owner: input.owner,
        operation: input.operation,
        mode: 'observe',
        certificationDecision: input.certificationDecision,
        certificationAllowed: input.certificationAllowed,
        runtimeAllowed: input.runtimeAllowed,
        reasonCode: input.reasonCode,
        reason: input.reason,
        sharedEvidenceId: sharedEvidence?.id ?? null,
      },
    });

    runtimeEvidenceRecorded = true;
  } catch {
    runtimeEvidenceRecorded = false;
  }

  return Object.freeze({
    sharedEvidence,
    runtimeEvidenceRecorded,
  });
}

export function observeCallsRuntimeHandler(
  owner: CallsRuntimeHandlerOwner,
): CallsRuntimeObservation {
  const result =
    evaluateCallsRuntimeHandlerCertification(owner, 'observe');

  const id = createObservationId();

  const publication = publishCallsObservationEvidence({
    id,
    owner,
    operation: result.operation,
    certificationDecision: result.certificationDecision,
    certificationAllowed: result.certificationAllowed,
    runtimeAllowed: result.runtimeAllowed,
    reasonCode: result.reasonCode,
    reason: result.reason,
    observedAt: result.evaluatedAt,
  });

  const observation: CallsRuntimeObservation = Object.freeze({
    id,
    bridge: 'CallsRuntimeObservationBridge',
    owner,
    operation: result.operation,
    mode: 'observe',
    certificationDecision: result.certificationDecision,
    certificationAllowed: result.certificationAllowed,
    runtimeAllowed: result.runtimeAllowed,
    reasonCode: result.reasonCode,
    reason: result.reason,
    observedAt: result.evaluatedAt,
    sharedEvidenceId: publication.sharedEvidence?.id ?? null,
    runtimeEvidenceRecorded:
      publication.runtimeEvidenceRecorded,
  });

  observations.push(observation);
  retainBoundedObservationHistory();

  try {
    emitCallsRuntimeEvent({
      source: 'CallsRuntimeObservationBridge',
      stage: 'CALLS_RUNTIME_HANDLER_OBSERVED',
      status:
        observation.certificationDecision === 'block'
          ? 'waiting'
          : 'success',
      observationId: observation.id,
      owner: observation.owner,
      operation: observation.operation,
      observationMode: observation.mode,
      certificationDecision:
        observation.certificationDecision,
      certificationAllowed:
        observation.certificationAllowed,
      runtimeAllowed: observation.runtimeAllowed,
      reasonCode: observation.reasonCode,
      reason: observation.reason,
      sharedEvidenceId:
        observation.sharedEvidenceId,
      runtimeEvidenceRecorded:
        observation.runtimeEvidenceRecorded,
      timestamp: observation.observedAt,
    });
  } catch {
    // Persistent publication must never interrupt a call.
  }

  notifyCallsRuntimeObservationListeners();

  return observation;
}

export function getCallsRuntimeObservations():
readonly CallsRuntimeObservation[] {
  return Object.freeze([...observations]);
}

export function subscribeCallsRuntimeObservations(
  listener: CallsRuntimeObservationListener,
): () => void {
  observationListeners.add(listener);

  try {
    listener(getCallsRuntimeObservations());
  } catch {
    // A failed consumer cannot interrupt Calls runtime.
  }

  return () => {
    observationListeners.delete(listener);
  };
}

export function clearCallsRuntimeObservations(): void {
  observations.splice(0, observations.length);
  notifyCallsRuntimeObservationListeners();
}
